import './styles/graphic.scss';
import rawStatements from './data/data.json';
import meta from './data/meta.json';

// Shared spectrum colors, matching the companion statement-timeline graphic.
const COLORS = {
  'Anti-Ukraine': '#EA9696',
  'Pro-Russia': '#EA9696',
  Neutral: '#E5B973',
  'Anti-Russia': '#93B5D6',
  'Pro-Ukraine': '#93B5D6',
};

// Vertical level per code: 2 = top (pro-Ukraine), 1 = middle, 0 = bottom (pro-Russia)
const LEVEL = {
  'Pro-Ukraine': 2,
  'Anti-Russia': 2,
  Neutral: 1,
  'Pro-Russia': 0,
  'Anti-Ukraine': 0,
};

// One row of metadata per band, top to bottom. `lines` is the axis label,
// wrapped across rows but styled uniformly as a single phrase.
const BANDS = [
  {
    level: 2, color: '#93B5D6', text: '#3f6b9c', lines: ['Pro-Ukraine/', 'Anti-Russia'],
  },
  {
    level: 1, color: '#E5B973', text: '#b1832f', lines: ['Neutral'],
  },
  {
    level: 0, color: '#EA9696', text: '#cf6363', lines: ['Pro-Russia/', 'Anti-Ukraine'],
  },
];

const MONTHS = [
  'Jan.', 'Feb.', 'March', 'April', 'May', 'June',
  'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
];

const SVG_NS = 'http://www.w3.org/2000/svg';

// Excel serial date (xlsx epoch) → JS Date, or pass through a real Date/string.
function toDate(raw) {
  if (raw instanceof Date) return raw;
  if (typeof raw === 'number') {
    return new Date(Math.round((raw - 25569) * 86400 * 1000));
  }
  const parts = String(raw).split('/');
  return new Date(Date.UTC(+parts[2], +parts[0] - 1, +parts[1]));
}

function formatDate(d) {
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

function el(name, attrs = {}) {
  const node = document.createElementNS(SVG_NS, name);
  Object.keys(attrs).forEach(k => node.setAttribute(k, attrs[k]));
  return node;
}

// Editorial copy is pulled from the Google Sheet "labels" tab (meta.json) so it
// can be tweaked without touching code; these fallbacks keep the chart intact if
// a key is missing or a data sync clobbers the file.
const ANNO = {
  springLabel: 'Spring/summer 2025',
  springBody: 'Trump repeatedly criticized Putin for prolonging the war.',
  alaskaBody: 'Criticism of Putin cooled after the Alaska Summit.',
};

function copy(key, fallback) {
  const v = meta && meta[key];
  return v != null && String(v).trim() !== '' ? String(v).trim() : fallback;
}

const LABEL_FONT = "500 12px 'Roboto Mono', monospace";
const BODY_FONT = "12px 'CNN Sans Display', 'CNN', sans-serif";

// Wrap arbitrary-length copy to a pixel width so sheet edits of any length still
// lay out cleanly. Uses canvas metrics (approximate but stable, no DOM needed).
let measureCtx = null;
function wrapText(text, font, maxWidth) {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
  measureCtx.font = font;
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = '';
  words.forEach((w) => {
    const trial = cur ? `${cur} ${w}` : w;
    if (cur && measureCtx.measureText(trial).width > maxWidth) {
      lines.push(cur);
      cur = w;
    } else {
      cur = trial;
    }
  });
  if (cur) lines.push(cur);
  return lines;
}

function textWidth(text, font) {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
  measureCtx.font = font;
  return measureCtx.measureText(String(text)).width;
}

// Build [{ text, cls }] callout lines: bold label line(s) then body line(s).
function calloutLines(label, body, maxWidth) {
  const out = [];
  wrapText(label, LABEL_FONT, maxWidth).forEach(t => out.push({ text: t, cls: 'anno-label' }));
  wrapText(body, BODY_FONT, maxWidth).forEach(t => out.push({ text: t, cls: 'anno-body' }));
  return out;
}

export default function graphic(props, container, isMobile) {
  container.html('');
  container.style('position', 'relative');

  const statements = rawStatements
    .filter(s => LEVEL[s.Code] !== undefined && s.Date != null)
    .map(s => ({ ...s, date: toDate(s.Date) }))
    .filter(s => !Number.isNaN(s.date.getTime()))
    .sort((a, b) => a.date - b.date);

  // Nothing usable (e.g. a data sync replaced the sheet with unrelated rows) —
  // bail out quietly instead of throwing.
  if (statements.length === 0) {
    if (window.pymChild) window.pymChild.sendHeight();
    return;
  }

  // Counts per band for the labels.
  const counts = { 2: 0, 1: 0, 0: 0 };
  statements.forEach((s) => { counts[LEVEL[s.Code]] += 1; });

  if (isMobile) {
    renderVertical(props, container, statements, counts);
  } else {
    renderHorizontal(props, container, statements, counts);
  }
}

// ═══ Desktop: horizontal timeline (time → x, position → y bands) ═════════════════
function renderHorizontal(props, container, statements, counts) {
  const { initialWidth } = props;
  const isMobile = false;

  // ── Dimensions ────────────────────────────────────────────────────────────
  const width = initialWidth;
  const margin = {
    top: 18,
    right: isMobile ? 12 : 16,
    bottom: 30,
    left: isMobile ? 100 : 108,
  };
  // The blue (top) and red (bottom) bands are extra tall to hold callouts in the
  // open space beyond their dots (blue above, red below).
  const topH = isMobile ? 158 : 150;
  const botH = topH;
  const midH = isMobile ? 60 : 80;
  const plotH = topH + midH + botH;
  const height = margin.top + plotH + margin.bottom;

  const plotLeft = margin.left;
  const plotRight = width - margin.right;
  const dotPad = 24; // keep first/last dots well off the band edges

  const t0 = statements[0].date.getTime();
  const t1 = statements[statements.length - 1].date.getTime();
  const x = t => plotLeft + dotPad
    + ((t - t0) / (t1 - t0)) * (plotRight - plotLeft - dotPad * 2);

  // Per-level band geometry: rect `top`/`h`, plus the `dot` row y. The blue and
  // red dots sit a fixed `neutralGap` from the neutral dividers (independent of
  // band height) so the zig-zag amplitude stays symmetric, leaving the taller
  // outer regions free for annotations.
  const neutralGap = isMobile ? 52 : 58;
  const midTop = margin.top + topH;
  const botTop = margin.top + topH + midH;
  const bandGeom = {
    2: { top: margin.top, h: topH, dot: midTop - neutralGap },
    1: { top: midTop, h: midH, dot: midTop + midH / 2 },
    0: { top: botTop, h: botH, dot: botTop + neutralGap },
  };
  const y = level => bandGeom[level].dot;

  const svg = el('svg', {
    width,
    height,
    viewBox: `0 0 ${width} ${height}`,
    class: 'summary-svg',
  });

  // ── Bands ─────────────────────────────────────────────────────────────────
  BANDS.forEach((band) => {
    const geom = bandGeom[band.level];
    svg.appendChild(el('rect', {
      x: plotLeft,
      y: geom.top,
      width: plotRight - plotLeft,
      height: geom.h,
      fill: band.color,
      'fill-opacity': band.level === 1 ? 0.1 : 0.14,
    }));

    // Left-axis label: the phrase (uniform style, wrapped) plus a count line,
    // vertically centered in the band.
    const cy = y(band.level);
    const nameSize = 12;
    const countSize = 12;
    const lineH = 16;
    const nLines = band.lines.length;
    const blockH = nLines * lineH + lineH; // names + one count line
    const firstBaseline = cy - blockH / 2 + nameSize;

    band.lines.forEach((txt, li) => {
      const name = el('text', {
        x: plotLeft - 10,
        y: firstBaseline + li * lineH,
        'text-anchor': 'end',
        class: 'band-name',
        fill: band.text,
        'font-size': nameSize,
      });
      name.textContent = txt;
      svg.appendChild(name);
    });

    const count = el('text', {
      x: plotLeft - 10,
      y: firstBaseline + nLines * lineH + 2,
      'text-anchor': 'end',
      class: 'band-count',
      'font-size': countSize,
    });
    count.textContent = `${counts[band.level]} statement${counts[band.level] === 1 ? '' : 's'}`;
    svg.appendChild(count);
  });

  // Divider lines between bands.
  [bandGeom[1].top, bandGeom[0].top].forEach((dy) => {
    svg.appendChild(el('line', {
      x1: plotLeft,
      x2: plotRight,
      y1: dy,
      y2: dy,
      stroke: '#ffffff',
      'stroke-width': 1,
    }));
  });

  // ── Year gridlines + labels ────────────────────────────────────────────────
  // Gridlines sit on Jan 1 boundaries; each year label is left-aligned just below
  // the line that marks where that year starts (first year anchors at the data's
  // left edge, since Jan 1 falls off-chart).
  const firstYear = statements[0].date.getUTCFullYear();
  const lastYear = statements[statements.length - 1].date.getUTCFullYear();
  for (let yr = firstYear; yr <= lastYear; yr += 1) {
    const jan1 = Date.UTC(yr, 0, 1);
    // Only years whose Jan 1 falls on-chart get a line + centered label; the
    // first (partial) year is omitted since its start is off-chart.
    if (jan1 > t0 && jan1 < t1) {
      const gx = x(jan1);
      svg.appendChild(el('line', {
        x1: gx, x2: gx, y1: margin.top, y2: margin.top + plotH, stroke: '#d9d9d9', 'stroke-width': 1, 'stroke-dasharray': '2 3',
      }));
      const lbl = el('text', {
        x: gx,
        y: margin.top + plotH + 18,
        'text-anchor': 'middle',
        class: 'year-label',
      });
      lbl.textContent = yr;
      svg.appendChild(lbl);
    }
  }

  // ── Connecting zig-zag line (per-segment gradient, echoing the main graphic) ─
  const defs = el('defs');
  svg.appendChild(defs);

  for (let i = 0; i < statements.length - 1; i += 1) {
    const a = statements[i];
    const b = statements[i + 1];
    const x1 = x(a.date.getTime());
    const y1 = y(LEVEL[a.Code]);
    const x2 = x(b.date.getTime());
    const y2 = y(LEVEL[b.Code]);

    const gradId = `grad-${i}`;
    const grad = el('linearGradient', {
      id: gradId, gradientUnits: 'userSpaceOnUse', x1, y1, x2, y2,
    });
    const s1 = el('stop', { offset: '0%', 'stop-color': COLORS[a.Code] });
    const s2 = el('stop', { offset: '100%', 'stop-color': COLORS[b.Code] });
    grad.appendChild(s1);
    grad.appendChild(s2);
    defs.appendChild(grad);

    svg.appendChild(el('line', {
      x1, y1, x2, y2, stroke: `url(#${gradId})`, 'stroke-width': isMobile ? 2 : 2.5, 'stroke-linecap': 'round',
    }));
  }

  // ── Dots ────────────────────────────────────────────────────────────────────
  statements.forEach((s) => {
    const cx = x(s.date.getTime());
    const cy = y(LEVEL[s.Code]);
    // Annotated dots (Alaska Summit + the spring/summer 2025 anti-Putin run) get
    // a dark border to stand out.
    const isAlaska = /Alaska/.test(s.Caption || '');
    const isCluster = s.Code === 'Anti-Russia' && s.date.getUTCFullYear() === 2025;
    const highlighted = isAlaska || isCluster;
    svg.appendChild(el('circle', {
      cx,
      cy,
      r: isMobile ? 5 : 6,
      fill: COLORS[s.Code],
      stroke: highlighted ? '#262626' : '#ffffff',
      'stroke-width': highlighted ? 2 : 1.5,
      class: 'summary-dot',
    }));
  });

  // Label the first and last dots with their exact dates, stacked onto two lines
  // (month/day, comma retained, over year). First dot above, last dot below.
  const endpoints = [
    { s: statements[0], anchor: 'start', dir: -1 },
    { s: statements[statements.length - 1], anchor: 'end', dir: 1 },
  ];
  const dotR = isMobile ? 5 : 6;
  endpoints.forEach(({ s, anchor, dir }) => {
    const [monthDay, year] = formatDate(s.date).split(', ');
    const cx = x(s.date.getTime());
    const cy = y(LEVEL[s.Code]);
    // Align the label's edge with the dot's outer edge (left edge for the first
    // dot, right edge for the last) rather than its center.
    const labelX = anchor === 'start' ? cx - dotR : cx + dotR;
    // Above: year sits nearest the dot; below: month/day sits nearest the dot.
    const nearY = dir === -1 ? cy - 13 : cy + 20;
    const farY = nearY + dir * 14;
    const rows = dir === -1
      ? [{ text: `${monthDay},`, y: farY }, { text: year, y: nearY }]
      : [{ text: `${monthDay},`, y: nearY }, { text: year, y: farY }];
    rows.forEach((row) => {
      const lbl = el('text', {
        x: labelX, y: row.y, 'text-anchor': anchor, class: 'dot-date-label',
      });
      lbl.textContent = row.text;
      svg.appendChild(lbl);
    });
  });

  // ── Annotation: spring–summer 2025 anti-Putin run ───────────────────────────
  // The blue cluster is a string of Anti-Russia statements in 2025; label it in
  // the open space above the dots, inside the blue band.
  const annoStmts = statements.filter(
    s => s.Code === 'Anti-Russia' && s.date.getUTCFullYear() === 2025,
  );
  if (annoStmts.length > 1) {
    const xs = annoStmts.map(s => x(s.date.getTime()));
    const midX = (Math.min(...xs) + Math.max(...xs)) / 2;

    const annoLines = calloutLines(
      copy('spring_label', ANNO.springLabel),
      copy('spring_body', ANNO.springBody),
      250,
    );

    const annoSize = 12;
    const lineGap = 15;
    const dotR = isMobile ? 5 : 6;
    // Text stacks upward in the open space above the blue cluster dots.
    const lastBaseline = y(2) - dotR - 18;
    annoLines.forEach((ln, li) => {
      ln.y = lastBaseline - (annoLines.length - 1 - li) * lineGap;
    });

    const half = isMobile ? 90 : 122;
    const textX = Math.max(half + 2, Math.min(midX, width - half - 2));

    const g = el('g', { class: 'annotation' });

    annoLines.forEach((ln) => {
      const t = el('text', {
        x: textX, y: ln.y, 'text-anchor': 'middle', class: ln.cls, 'font-size': annoSize,
      });
      t.textContent = ln.text;
      g.appendChild(t);
    });

    svg.appendChild(g);
  }

  // ── Annotation: Alaska Summit ───────────────────────────────────────────────
  // A single red (Pro-Russia) dot; label it in the open space below, inside the
  // red band, with a grey connector up to the dot.
  const alaska = statements.find(s => /Alaska/.test(s.Caption || ''));
  if (alaska) {
    const cx = x(alaska.date.getTime());
    const cy = y(LEVEL[alaska.Code]);
    const dotR = isMobile ? 5 : 6;
    const annoSize = 12;
    const lineGap = 15;

    const annoLines = calloutLines(
      formatDate(alaska.date),
      copy('alaska_body', ANNO.alaskaBody),
      190,
    );

    // Stack downward from a title baseline below the dot.
    const firstBaseline = cy + dotR + 18;
    annoLines.forEach((ln, li) => { ln.y = firstBaseline + li * lineGap; });

    const half = isMobile ? 76 : 92;
    const textX = Math.max(half + 2, Math.min(cx, width - half - 2));

    const g = el('g', { class: 'annotation' });

    annoLines.forEach((ln) => {
      const t = el('text', {
        x: textX, y: ln.y, 'text-anchor': 'middle', class: ln.cls, 'font-size': annoSize,
      });
      t.textContent = ln.text;
      g.appendChild(t);
    });

    svg.appendChild(g);
  }

  container.node().appendChild(svg);

  if (window.pymChild) window.pymChild.sendHeight();
}

// ═══ Mobile: vertical timeline (time → y, position → x columns) ══════════════════
function renderVertical(props, container, statements, counts) {
  const { initialWidth } = props;
  const width = initialWidth;

  const headerH = 62;
  const marginBottom = 30;
  const timeLen = 510;
  const dotPad = 22;
  const dotR = 6.5;
  const annoSize = 12;
  const lineGap = 15;

  // Columns span the full width; the callouts float on top of the tinted bands
  // in the open pockets between dots, mirroring the on-chart desktop treatment.
  const plotLeft = 0;
  const plotRight = width;
  const colW = width / 3;
  const plotTop = headerH;
  const plotBottom = plotTop + timeLen;
  const height = plotBottom + marginBottom;

  const t0 = statements[0].date.getTime();
  const t1 = statements[statements.length - 1].date.getTime();
  const timeY = t => plotTop + dotPad + ((t - t0) / (t1 - t0)) * (timeLen - dotPad * 2);
  // Columns left→right: blue (level 2), neutral (1), red (0).
  const colX = level => plotLeft + colW * ((2 - level) + 0.5);

  const svg = el('svg', {
    width, height, viewBox: `0 0 ${width} ${height}`, class: 'summary-svg',
  });

  // ── Columns ─────────────────────────────────────────────────────────────────
  BANDS.forEach((band) => {
    const cx = colX(band.level);
    svg.appendChild(el('rect', {
      x: cx - colW / 2,
      y: plotTop,
      width: colW,
      height: timeLen,
      fill: band.color,
      'fill-opacity': band.level === 1 ? 0.1 : 0.14,
    }));
  });

  // Dividers between columns.
  [1, 2].forEach((i) => {
    const dx = plotLeft + colW * i;
    svg.appendChild(el('line', {
      x1: dx, x2: dx, y1: plotTop, y2: plotBottom, stroke: '#ffffff', 'stroke-width': 1,
    }));
  });

  // ── Header legend (three items across full width) ───────────────────────────
  // Bottom-align the label lines so shorter labels (Neutral) share a baseline
  // with the last line of the taller ones, and all counts sit on one row.
  const maxLines = Math.max(...BANDS.map(b => b.lines.length));
  const countY = 16 + maxLines * 16 + 2;
  BANDS.forEach((band) => {
    const hx = width * ((2 - band.level) + 0.5) / 3;
    const offset = maxLines - band.lines.length;
    band.lines.forEach((txt, li) => {
      const name = el('text', {
        x: hx, y: 16 + (offset + li) * 16, 'text-anchor': 'middle', class: 'band-name', fill: band.text, 'font-size': 12,
      });
      name.textContent = txt;
      svg.appendChild(name);
    });
    const count = el('text', {
      x: hx,
      y: countY,
      'text-anchor': 'middle',
      class: 'band-count',
      'font-size': 12,
    });
    count.textContent = `${counts[band.level]} statement${counts[band.level] === 1 ? '' : 's'}`;
    svg.appendChild(count);
  });

  // ── Year gridlines + labels (down the left) ─────────────────────────────────
  // Each year whose Jan 1 falls on-chart gets a line, with the label floating just
  // above it; the first (partial) year is omitted since its start is off-chart.
  const firstYear = statements[0].date.getUTCFullYear();
  const lastYear = statements[statements.length - 1].date.getUTCFullYear();
  for (let yr = firstYear; yr <= lastYear; yr += 1) {
    const jan1 = Date.UTC(yr, 0, 1);
    if (jan1 > t0 && jan1 < t1) {
      const gy = timeY(jan1);
      svg.appendChild(el('line', {
        x1: plotLeft, x2: plotRight, y1: gy, y2: gy, stroke: '#d9d9d9', 'stroke-width': 1, 'stroke-dasharray': '2 3',
      }));
      // The spring/summer callout occupies the upper-left, so flag its year (2025)
      // to the right edge instead so it isn't covered.
      const onRight = yr === 2025;
      const lbl = el('text', {
        x: onRight ? plotRight - 4 : 4,
        y: gy - 5,
        'text-anchor': onRight ? 'end' : 'start',
        class: 'year-label',
      });
      lbl.textContent = yr;
      svg.appendChild(lbl);
    }
  }

  // ── Connecting zig-zag line ──────────────────────────────────────────────────
  const defs = el('defs');
  svg.appendChild(defs);
  for (let i = 0; i < statements.length - 1; i += 1) {
    const a = statements[i];
    const b = statements[i + 1];
    const x1 = colX(LEVEL[a.Code]);
    const y1 = timeY(a.date.getTime());
    const x2 = colX(LEVEL[b.Code]);
    const y2 = timeY(b.date.getTime());
    const gradId = `vgrad-${i}`;
    const grad = el('linearGradient', {
      id: gradId, gradientUnits: 'userSpaceOnUse', x1, y1, x2, y2,
    });
    grad.appendChild(el('stop', { offset: '0%', 'stop-color': COLORS[a.Code] }));
    grad.appendChild(el('stop', { offset: '100%', 'stop-color': COLORS[b.Code] }));
    defs.appendChild(grad);
    svg.appendChild(el('line', {
      x1, y1, x2, y2, stroke: `url(#${gradId})`, 'stroke-width': 2, 'stroke-linecap': 'round',
    }));
  }

  // Annotation ticks live in their own layer, added before the dots so they
  // render behind the circles.
  const tickLayer = el('g');
  svg.appendChild(tickLayer);

  // ── Dots ────────────────────────────────────────────────────────────────────
  statements.forEach((s) => {
    const cx = colX(LEVEL[s.Code]);
    const cy = timeY(s.date.getTime());
    const isAlaska = /Alaska/.test(s.Caption || '');
    const isCluster = s.Code === 'Anti-Russia' && s.date.getUTCFullYear() === 2025;
    const highlighted = isAlaska || isCluster;
    svg.appendChild(el('circle', {
      cx,
      cy,
      r: dotR,
      fill: COLORS[s.Code],
      stroke: highlighted ? '#262626' : '#ffffff',
      'stroke-width': highlighted ? 2 : 1.5,
      class: 'summary-dot',
    }));
  });

  // ── First/last exact-date labels (stacked month over year) ──────────────────
  const first = statements[0];
  const last = statements[statements.length - 1];
  // First dot (top): single-line date to the right of the dot.
  (() => {
    const cx = colX(LEVEL[first.Code]);
    const cy = timeY(first.date.getTime());
    const lbl = el('text', {
      x: cx + dotR + 6, y: cy + 4, 'text-anchor': 'start', class: 'dot-date-label',
    });
    lbl.textContent = formatDate(first.date);
    svg.appendChild(lbl);
  })();
  // Last dot (bottom): single-line date to the right of the dot, in line with it.
  (() => {
    const cx = colX(LEVEL[last.Code]);
    const cy = timeY(last.date.getTime());
    const lbl = el('text', {
      x: cx + dotR + 6, y: cy + 4, 'text-anchor': 'start', class: 'dot-date-label',
    });
    lbl.textContent = formatDate(last.date);
    svg.appendChild(lbl);
  })();

  // ── Annotation: spring/summer 2025 run (blue column) ────────────────────────
  // Stacks in the big empty pocket directly above the blue cluster, on the chart.
  const cluster = statements.filter(
    s => s.Code === 'Anti-Russia' && s.date.getUTCFullYear() === 2025,
  );
  if (cluster.length > 1) {
    const topY = Math.min(...cluster.map(s => timeY(s.date.getTime())));
    // Wrap the body no wider than the title line above it.
    const springLabel = copy('spring_label', ANNO.springLabel);
    const lines = [
      { text: springLabel, cls: 'anno-label' },
      ...wrapText(copy('spring_body', ANNO.springBody), BODY_FONT, textWidth(springLabel, LABEL_FONT))
        .map(t => ({ text: t, cls: 'anno-body' })),
    ];
    const n = lines.length;
    const textX = 6;
    const bottomBaseline = topY - dotR - 16;
    const g = el('g', { class: 'annotation' });
    lines.forEach((ln, i) => {
      const t = el('text', {
        x: textX, y: bottomBaseline - (n - 1 - i) * lineGap, 'text-anchor': 'start', class: `${ln.cls} anno-strong`, 'font-size': annoSize,
      });
      t.textContent = ln.text;
      g.appendChild(t);
    });
    // Tick connecting the callout to the top cluster dot (behind the circles).
    tickLayer.appendChild(el('line', {
      x1: colX(2), y1: topY, x2: colX(2), y2: bottomBaseline + 5, stroke: '#bfbfbf', 'stroke-width': 1,
    }));
    svg.appendChild(g);
  }

  // ── Annotation: Alaska Summit (red column) ──────────────────────────────────
  // Sits in the clear center pocket beside the Alaska dot, with a connector.
  const alaska = statements.find(s => /Alaska/.test(s.Caption || ''));
  if (alaska) {
    const yA = timeY(alaska.date.getTime());
    // Narrow the wrap on small (~320px) phones so the body breaks to three lines.
    const lines = calloutLines(
      formatDate(alaska.date),
      copy('alaska_body', ANNO.alaskaBody),
      width <= 340 ? 120 : 160,
    );
    const n = lines.length;
    const textRight = width - 6;
    // Stack upward so the block sits above the dot.
    const bottomBaseline = yA - dotR - 14;
    const g = el('g', { class: 'annotation' });
    lines.forEach((ln, i) => {
      const t = el('text', {
        x: textRight, y: bottomBaseline - (n - 1 - i) * lineGap, 'text-anchor': 'end', class: `${ln.cls} anno-strong`, 'font-size': annoSize,
      });
      t.textContent = ln.text;
      g.appendChild(t);
    });
    // Tick connecting the callout to the Alaska dot (behind the circles).
    tickLayer.appendChild(el('line', {
      x1: colX(0), y1: yA, x2: colX(0), y2: bottomBaseline + 5, stroke: '#bfbfbf', 'stroke-width': 1,
    }));
    svg.appendChild(g);
  }

  container.node().appendChild(svg);

  if (window.pymChild) window.pymChild.sendHeight();
}
